import qs from 'query-string';
import history from '../history';
import { updateSearch } from '../helpers/querystring';
import { PARTNERS } from '../constants';
import {
  getFirstYearFromWeeks, getSelectedDestination, getSelectedCrew, getSelectedParams
} from '../helpers/week';
import { calculateRatio, maxCapacityCrew } from '../helpers/crew';
import { getPricePerPerson, resolvePerPersonPriceFilterRange } from '../helpers/pricing';
import { getSelectedFilters } from '../helpers/filter';
import { deNormalizeData } from '../helpers/yachts';
export const fetchYachts = (companyId = null) => () =>
  fetch(`/api/yl/slim${companyId ? `?company=${companyId}` : ''}`, {
    credentials: 'same-origin'
  })
    .then(response => response.json())
    .then(response => {
      const yachts = deNormalizeData(response.data);
      return { data: yachts };
    })
    .catch(ex => console.log('failed fetching yacht weeks...', ex));

// this function filters yachts based on stuff selected throughout the funnel(like when you select destination, week)
// and not using filter values (for that we use filterYachts)
export const filterYachtsByWeek = destinations => dispatch => {
  const destination = getSelectedDestination(destinations, history.location);
  let { year } = getSelectedParams(history.location);
  const { weeks } = getSelectedParams(history.location);
  if (!destination) {
    return;
  }

  if (!year) {
    year = getFirstYearFromWeeks(destination.weeks);
  }
  const selectedWeekIds = destination.weeks
    .filter(w => weeks.includes(w.week) && w.year === year)
    .map(w => w.id);

  const allWeekIds = destination.weeks.map(item => item.id);

  dispatch({
    type: 'FILTER_YACHTS_BY_WEEK',
    payload: {
      weeks: selectedWeekIds.length === 0 ? allWeekIds : selectedWeekIds,
      destination
    }
  });
};

/**
 * Partners a Quarterdeck referral sees: Dream Yacht only, when the selected destination has Dream Yacht
 * yacht-weeks; otherwise every partner. Read from the yacht-week assignments, so any destination works,
 * festival destinations included, without a destination id check.
 *
 * @param {object|null|undefined} destination selected destination
 * @param {object[]|null|undefined} yachtItems yacht-weeks in the store (week.destinationId, boat.partner.id)
 * @returns {number[]} partner ids to keep; empty means no partner filter
 */
export const getQdReferralPartners = (destination, yachtItems) => {
  if (!destination) {
    return [];
  }
  const dreamYacht = [PARTNERS.DREAM_YACHT_CROATIA, PARTNERS.DREAM_YACHT_GREECE];
  const hasDreamYacht = (yachtItems || []).some(item => item.week?.destinationId === destination.id
    && dreamYacht.includes(item.boat?.partner?.id));
  return hasDreamYacht ? dreamYacht : [];
};

// this is filtering yachts based on filter values
export const filterYachts = () => (dispatch, getState) => {
  const { yachts, destinations } = getState();
  const {
    hullType, minCapacity, maxCapacity, marina, amenities, weekFilter, destinationFilter, yearFilter, boatYear, boatCabins, boatSize, minPrice, maxPrice, favourites = null, countryFilter, festivalFilter, deals = null
  } = getSelectedFilters(history.location);
  const crew = getSelectedCrew(destinations.items, location);
  // For yachts filtering host is always 0:
  crew.hostess = 0;
  crew.total = Object.keys(crew).filter(i => !['total', 'ratio'].includes(i)).reduce((acc, value) => acc + crew[value], 0);
  crew.ratio = calculateRatio(crew);
  const cheapestYacht = yachts.cheapestYacht;
  const mostExpensiveYacht = yachts.mostExpensiveYacht;
  const cheapestPerPerson = cheapestYacht ? getPricePerPerson(cheapestYacht, maxCapacityCrew(cheapestYacht.boat.capacity)) : null;
  const mostExpensivePerPerson = mostExpensiveYacht ? getPricePerPerson(mostExpensiveYacht, maxCapacityCrew(mostExpensiveYacht.boat.capacity)) : null;
  const { min: rangeMin, max: rangeMax } = resolvePerPersonPriceFilterRange(
    minPrice,
    maxPrice,
    cheapestPerPerson,
    mostExpensivePerPerson
  );
  const priceRange = {
    min: rangeMin,
    max: rangeMax
  };
  let partners = [];
  if (location) {
    // QD referrals are for Dream Yachts only: when the selected destination has Dream Yacht yacht-weeks, show
    // just those. Read from the yacht-week assignments, so any destination (festivals included) works.
    const qdReferral = qs.parse(location.search).referral === 'qd';
    if (qdReferral) {
      partners = getQdReferralPartners(getSelectedDestination(destinations.items, location), yachts.items);
    }
  }
  dispatch({
    type: 'FILTER_YACHTS',
    payload: {
      hullType,
      amenities,
      marina,
      capacity: { minCapacity, maxCapacity },
      boatYear,
      boatCabins,
      boatSize,
      priceRange,
      weekFilter,
      destinationFilter,
      yearFilter,
      partners,
      favourites,
      countryFilter,
      festivalFilter,
      deals
    }
  });
};

export const sortBy = (criteria, location) => dispatch => {
  dispatch({
    type: 'SORT_BY',
    criteria
  });

  history.replace({
    pathname: location.pathname,
    search: updateSearch(location, {
      criteria: criteria.value
    })
  });
};

export const fetchBoatWeek = boatWeekId => dispatch =>
  fetch(`/api/yl/yacht-weeks/${boatWeekId}`, {
    credentials: 'same-origin'
  })
    .then(response => response.json())
    .then(json => {
      const yacht = json.data;
      yacht.fullyLoaded = true;
      dispatch({
        type: 'SELECT_YACHT',
        yacht
      });
      return yacht;
    });

export const fetchYachtGroup = yachtGroupId => () =>
  fetch(`/api/yl/yacht-group/${yachtGroupId}`, {
    credentials: 'same-origin'
  })
    .then(response => response.json());

export const buildYachtOptions = (boatWeeks, plans = null) => dispatch => {
  const cheapestYacht = boatWeeks.length > 0 ? boatWeeks.reduce((prev, curr) => {
    const prevPrice = getPricePerPerson(prev, maxCapacityCrew(prev.boat.capacity));
    const currPrice = getPricePerPerson(curr, maxCapacityCrew(curr.boat.capacity));
    return prev && prevPrice < currPrice ? prev : curr;
  }, boatWeeks[0]) : null;
  const mostExpensiveYacht = boatWeeks.length > 0 ? boatWeeks.reduce((prev, curr) => {
    const prevPrice = getPricePerPerson(prev, maxCapacityCrew(prev.boat.capacity));
    const currPrice = getPricePerPerson(curr, maxCapacityCrew(curr.boat.capacity));
    return prev && prevPrice > currPrice ? prev : curr;
  }, boatWeeks[0]) : null;
  return dispatch({
    type: 'SAVE_YACHTS',
    cheapestYacht,
    mostExpensiveYacht,
    boatWeeks: boatWeeks.map(boatWeek => ({
      ...boatWeek,
      isSavedPlan: plans && plans.some(plan => plan.boatWeek?.id === boatWeek.id)
    }))
  });
};